import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Select } from "antd";
// import { SelectRef } from "antd/lib/select";
import { useMemo, useRef } from "react";

import { SYSTEM_ATTRIBUTE_PROPERTY } from "~/core/constants/attributes";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { SELECT_INPUT_FOCUS_DELAY } from "../../constants";
import { useGlobalNewOpportunityModalContext } from "../../context";
import { useContactMultiSelect } from "../../hooks/useContactMultiSelect";
import { useGlobalCreateOpportunityStore } from "../../store";
import DDClearIcon from "../DDClearIcon";

import { AccountLabel } from "./AccountLabel";
import { AddContact } from "./AddContact";
import { DefaultDDRenderer } from "./DefaultDDRenderer";
import { MultiSelectLabelTag } from "./MultiSelectLabelTag";

export default function ContactMultiSelect() {
  const { form } = useGlobalNewOpportunityModalContext();
  const ref = useRef<any>(null);
  const {
    openSelect,
    setOpenSelect,
    ensureArray,
    handleSelect,
    searchQuery,
    setSearchQuery,
  } = useContactMultiSelect();
  const { multiSelectContacts, resetMultiSelectContactSelection } =
    useGlobalCreateOpportunityStore();

  const isSearchEnabled = useMemo(() => {
    return searchQuery?.trim() !== "";
  }, [searchQuery]);

  const handleDropdownClose = () => {
    setOpenSelect(false);
    // Focus the select after closing
    setTimeout(() => {
      ref.current?.focus();
    }, SELECT_INPUT_FOCUS_DELAY);
  };

  const handleTagClose = (e: React.MouseEvent, value: any) => {
    // Prevent the click event from bubbling up
    e.preventDefault();
    e.stopPropagation();

    const targetValue = multiSelectContacts.find(
      contact => contact.id === value,
    );
    if (targetValue) {
      handleSelect(targetValue);
    }
  };

  return (
    <Form.Item
      name={SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS}
      label="Other Stakeholders"
      normalize={ensureArray}
      getValueFromEvent={value => ensureArray(value)}
    >
      <Select
        onClick={e => {
          // Check for both the close icon class and SVG path
          const target = e.target as HTMLElement;
          const isCloseIcon =
            target.classList.contains("anticon-close") ||
            target.closest(".anticon-close");

          if (isCloseIcon) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        onFocus={() => {
          setOpenSelect(true);
        }}
        allowClear={{
          clearIcon: <DDClearIcon />,
        }}
        onClear={() => {
          form?.resetFields([
            SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS,
          ]);
          resetMultiSelectContactSelection();
          setOpenSelect(true);
        }}
        placeholder="Select other stakeholders"
        ref={ref}
        mode="multiple"
        showSearch={false}
        tagRender={event => {
          return (
            <MultiSelectLabelTag
              value={event.value}
              label={event.label}
              onTagClose={handleTagClose}
            />
          );
        }}
        onDropdownVisibleChange={setOpenSelect}
        open={openSelect}
        labelInValue={false}
        labelRender={e => {
          const contacts = multiSelectContacts.find(
            contact => contact.id === e.value,
          );
          return <AccountLabel item={contacts?.metadata} />;
        }}
        suffixIcon={
          multiSelectContacts.filter(contact => contact.active).length > 0 ? (
            <DDClearIcon asSuffix />
          ) : (
            <FontAwesomeIcon icon={faChevronDown} />
          )
        }
        value={form?.getFieldValue(
          SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS,
        )}
        dropdownRender={() => (
          <DefaultDDRenderer
            isMultiSelect={true}
            items={multiSelectContacts}
            onSearch={value => {
              setSearchQuery(value);
              setOpenSelect(true);
            }}
            onItemSelect={handleSelect}
            handleMultiSelect={handleSelect}
            emptyContent={
              <AddContact
                isSearchEnabled={isSearchEnabled}
                inputValue={searchQuery}
                setIsOpenDropdown={setOpenSelect}
                isMultiContact
              />
            }
            onCommandKeydown={e => {
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                handleDropdownClose();
              }
            }}
          />
        )}
      />
    </Form.Item>
  );
}
